import { html, LitElement } from 'lit'
import { state } from 'lit/decorators.js'

type Constructor<T = object> = new (...args: any[]) => T

export declare class HeadingSlotMixinInterface {
  hasHeading: boolean
  renderHeadingSlot(): unknown
}

/**
 * Gives a component a `slot="heading"` with identical hide-when-empty behaviour,
 * instead of every component hand-rolling its own slotchange/assignedNodes logic.
 *
 * Component side:
 * ```ts
 * @customElement('csc-store-stock')
 * export class CSCStoreStock extends HeadingSlotMixin(LitElement) {
 *   override render() {
 *     return html`
 *       ${this.renderHeadingSlot()}
 *       <div class="content">...</div>
 *     `
 *   }
 * }
 * ```
 *
 * Consumer side — fill `slot="heading"` to show one, omit the child entirely for none:
 * ```html
 * <csc-store-stock>
 *   <cse-text slot="heading" as="h3" variant="heading-3">Store availability</cse-text>
 * </csc-store-stock>
 * ```
 */
export const HeadingSlotMixin = <T extends Constructor<LitElement>>(superClass: T) => {
  class HeadingSlotMixinClass extends superClass {
    @state()
    hasHeading = false

    private _onHeadingSlotChange(event: Event) {
      const slot = event.target as HTMLSlotElement
      this.hasHeading = slot.assignedNodes({ flatten: true }).length > 0
    }

    renderHeadingSlot() {
      return html`
        <div class="heading" ?hidden=${!this.hasHeading}>
          <slot name="heading" @slotchange=${this._onHeadingSlotChange}></slot>
        </div>
      `
    }
  }

  return HeadingSlotMixinClass as Constructor<HeadingSlotMixinInterface> & T
}
